import React, { useState } from 'react';

function LinksStep({ data, updateData, nextStep, skipStep, isOptional }) {
  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    const links = { ...data.professionalLinks };
    links[field] = value;
    updateData({ professionalLinks: links });
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateURL = (url, field) => {
    if (!url) return true; // Optional field
    
    const urlPattern = /^https?:\/\/.+/;
    if (!urlPattern.test(url)) {
      return `Please enter a valid URL starting with http:// or https://`;
    }
    
    // Field-specific validation
    if (field === 'linkedin' && !url.includes('linkedin.com')) {
      return 'Please enter a valid LinkedIn URL';
    }
    if (field === 'github' && !url.includes('github.com')) {
      return 'Please enter a valid GitHub URL';
    }
    
    return true;
  };

  const validateAndNext = () => {
    const newErrors = {};
    const links = data.professionalLinks || {};
    
    // Validate each field
    ['linkedin', 'github', 'website'].forEach(field => {
      const validation = validateURL(links[field], field);
      if (validation !== true) {
        newErrors[field] = validation;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    nextStep();
  };

  const links = data.professionalLinks || {};

  return (
    <div className="bg-white bg-opacity-5 rounded-lg border border-gray-200 border-opacity-20 p-8 md:p-12">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-white mb-3">
          Add your <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">professional links</span>
        </h2>
        <p className="text-gray-400">
          Help employers learn more about your work and experience
        </p>
        {isOptional && (
          <div className="inline-flex items-center mt-3 px-3 py-1 bg-gray-700 bg-opacity-50 rounded-full">
            <span className="text-gray-300 text-sm">Optional - you can skip this step</span>
          </div>
        )}
      </div>

      <div className="max-w-lg mx-auto space-y-6">
        {/* LinkedIn */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2 text-blue-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
              LinkedIn Profile
            </div>
          </label>
          <input
            type="url"
            value={links.linkedin || ''}
            onChange={(e) => handleInputChange('linkedin', e.target.value)}
            className={`w-full bg-white bg-opacity-10 border rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:bg-opacity-15 transition-all duration-200 ${
              errors.linkedin 
                ? 'border-red-500 focus:border-red-400' 
                : 'border-gray-200 border-opacity-20 focus:border-blue-500'
            }`}
            placeholder="https://linkedin.com/in/yourprofile"
          />
          {errors.linkedin && (
            <p className="text-red-400 text-sm mt-1">{errors.linkedin}</p>
          )}
        </div>

        {/* GitHub */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
              </svg>
              GitHub Profile
            </div>
          </label>
          <input
            type="url"
            value={links.github || ''}
            onChange={(e) => handleInputChange('github', e.target.value)}
            className={`w-full bg-white bg-opacity-10 border rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:bg-opacity-15 transition-all duration-200 ${
              errors.github 
                ? 'border-red-500 focus:border-red-400' 
                : 'border-gray-200 border-opacity-20 focus:border-purple-500'
            }`}
            placeholder="https://github.com/yourusername"
          />
          {errors.github && (
            <p className="text-red-400 text-sm mt-1">{errors.github}</p>
          )}
        </div>

        {/* Personal Website */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9-9a9 9 0 00-9 9m0 0a9 9 0 009 9" />
              </svg>
              Personal Website/Portfolio
            </div>
          </label>
          <input
            type="url"
            value={links.website || ''}
            onChange={(e) => handleInputChange('website', e.target.value)}
            className={`w-full bg-white bg-opacity-10 border rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:bg-opacity-15 transition-all duration-200 ${
              errors.website 
                ? 'border-red-500 focus:border-red-400' 
                : 'border-gray-200 border-opacity-20 focus:border-green-500'
            }`}
            placeholder="https://yourwebsite.com"
          />
          {errors.website && (
            <p className="text-red-400 text-sm mt-1">{errors.website}</p>
          )}
        </div>

        {/* Action Buttons */}
        <div className="pt-6 space-y-3">
          <button
            onClick={validateAndNext}
            className="w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white font-medium rounded-lg hover:from-blue-600 hover:to-purple-600 transition-all duration-200"
          >
            Continue
          </button>
          
          {isOptional && (
            <button
              onClick={skipStep}
              className="w-full px-6 py-3 bg-gray-700 text-gray-300 font-medium rounded-lg hover:bg-gray-600 transition-all duration-200"
            >
              Skip for now
            </button>
          )}
        </div>
      </div>

      {/* Help Text */}
      <div className="text-center mt-8">
        <p className="text-gray-500 text-sm">
          {isOptional 
            ? "These links help showcase your work to potential employers"
            : "You can always add or update these links later in your profile"
          }
        </p>
      </div>
    </div>
  );
}

export default LinksStep; 